import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import {
  Loader2, X, Upload, FileText, Trash2, ChevronDown, Sparkles, CheckCircle2,
  ChevronUp, AlertCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { STANDARD_INDUSTRIES, INDUSTRY_CATEGORIES, getIndustryLabel } from '../../constants/industries';
import { ScrollArea } from '../ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRIES = [
  { code: 'GCC', en: 'GCC-Wide' },
  { code: 'SA', en: 'Saudi Arabia' },
  { code: 'AE', en: 'UAE' },
  { code: 'OM', en: 'Oman' },
  { code: 'KW', en: 'Kuwait' },
  { code: 'BH', en: 'Bahrain' },
  { code: 'QA', en: 'Qatar' },
];

const REPORT_TYPES = [
  { value: 'country', en: 'Country Report' },
  { value: 'industry', en: 'Industry Report' },
  { value: 'thematic', en: 'Thematic Report' },
  { value: 'insight', en: 'Insight (Free)' },
];

const EMPTY_FORM = {
  title: '', type: 'country', country: 'GCC', industries: [],
  price: 299, pages: 30, summary: '', highlights: [], is_featured: false,
  pdf_url: '', related_report_id: '',
};

export function AdminReportDialog({ open, onOpenChange, report, token, language, onSuccess }) {
  const isEdit = !!report;

  // AI extraction state (create mode only)
  const [aiState, setAiState] = useState('idle'); // idle | uploading | done | error
  const [tempFileId, setTempFileId] = useState(null);
  const [pdfFilename, setPdfFilename] = useState(null);
  const [showAllFields, setShowAllFields] = useState(false);

  // PDF management state (edit mode)
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfInfo, setPdfInfo] = useState({ filename: null, size: null, hasFile: false });
  // WO-63 — the white-label Word file (Enterprise customers buy it).
  const [docxUploading, setDocxUploading] = useState(false);
  const [docxInfo, setDocxInfo] = useState({ filename: null, size: null, pictures: 0, uploadedAt: null, hasFile: false });
  const [docxProblems, setDocxProblems] = useState(null);
  const docxInputRef = useRef(null);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [highlightInput, setHighlightInput] = useState('');
  const [showIndustryPicker, setShowIndustryPicker] = useState(false);
  const [loading, setLoading] = useState(false);

  const pdfInputRef = useRef(null);
  const analyzeInputRef = useRef(null);

  // Reset on open/close
  useEffect(() => {
    if (!open) return;
    setHighlightInput('');
    setShowIndustryPicker(false);

    if (report) {
      // Edit mode — pre-fill form
      setFormData({
        title: report.title || '',
        type: report.type || 'country',
        country: report.country || 'GCC',
        industries: report.industries || [],
        price: report.price ?? 299,
        pages: report.pages || 30,
        summary: report.summary || '',
        highlights: report.highlights || [],
        is_featured: report.isFeatured || false,
        pdf_url: report.pdf_url || '',
        related_report_id: report.related_report_id || '',
      });
      setPdfInfo({
        filename: report.pdf_filename || null,
        size: report.pdf_size || null,
        hasFile: !!report.pdf_file_id,
      });
      setDocxInfo({
        filename: report.docx_filename || null,
        size: report.docx_size || null,
        pictures: report.docx_pictures || 0,
        uploadedAt: report.docx_uploaded_at || null,
        hasFile: !!report.docx_file_id,
      });
      setDocxProblems(null);
      setAiState('idle');
      setTempFileId(null);
      setPdfFilename(null);
      setShowAllFields(true);
    } else {
      // Create mode — start fresh
      setFormData({ ...EMPTY_FORM });
      setAiState('idle');
      setTempFileId(null);
      setPdfFilename(null);
      setShowAllFields(false);
    }
  }, [open, report]);

  // ---- AI PDF Analysis --------------------------------------------------
  const handleAnalyzePdf = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    setAiState('uploading');
    setPdfFilename(file.name);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/analyze-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Analysis failed');

      // Auto-fill all form fields from AI response
      setFormData(prev => ({
        ...prev,
        title: data.title || '',
        type: data.suggested_type || 'country',
        country: data.suggested_country || 'GCC',
        industries: data.suggested_industries || [],
        pages: data.page_count || 30,
        summary: data.summary || '',
        highlights: data.highlights || [],
        price: 299,
      }));
      setTempFileId(data.temp_file_id);
      setAiState('done');
      toast.success('AI extracted report info — review and save');
    } catch (err) {
      setAiState('error');
      toast.error(`Analysis failed: ${err.message}`);
    } finally {
      if (analyzeInputRef.current) analyzeInputRef.current.value = '';
    }
  };

  // ---- PDF Upload for Edit mode -----------------------------------------
  const handlePdfUpload = async (file) => {
    if (!file || !isEdit) return;
    setPdfUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/${report._id}/pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setPdfInfo({ filename: data.pdf_filename, size: data.pdf_size, hasFile: true });
      toast.success('PDF uploaded');
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPdfUploading(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handlePdfDelete = async () => {
    if (!isEdit || !pdfInfo.hasFile) return;
    if (!window.confirm('Remove the uploaded PDF?')) return;
    setPdfUploading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/${report._id}/pdf`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Delete failed');
      setPdfInfo({ filename: null, size: null, hasFile: false });
      toast.success('PDF removed');
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPdfUploading(false);
    }
  };

  // ---- WO-63 — white-label Word file --------------------------------------
  const handleDocxUpload = async (file) => {
    if (!file || !isEdit) return;
    setDocxUploading(true);
    setDocxProblems(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/whitelabel/reports/${report._id}/docx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data.detail;
        if (d && typeof d === 'object') {
          setDocxProblems({ message: d.message, problems: d.problems || [] });
        } else {
          setDocxProblems({ message: (typeof d === 'string' && d) || 'Upload failed', problems: [] });
        }
        return;
      }
      setDocxInfo({
        filename: data.docx_filename, size: data.docx_size, pictures: data.docx_pictures || 0,
        uploadedAt: data.docx_uploaded_at, hasFile: true,
      });
      toast.success('Word file accepted — no Mid2Bid or Pastoria name found');
      onSuccess?.();
    } catch (err) {
      setDocxProblems({ message: 'Could not reach the server. Nothing was uploaded.', problems: [] });
    } finally {
      setDocxUploading(false);
      if (docxInputRef.current) docxInputRef.current.value = '';
    }
  };

  const handleDocxDelete = async () => {
    if (!isEdit || !docxInfo.hasFile) return;
    if (!window.confirm('Remove the white-label Word file? Enterprise customers will not be able to buy or download it until a new one is uploaded.')) return;
    setDocxUploading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/whitelabel/reports/${report._id}/docx`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Delete failed');
      setDocxInfo({ filename: null, size: null, pictures: 0, uploadedAt: null, hasFile: false });
      toast.success('Word file removed');
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDocxUploading(false);
    }
  };

  // ---- Highlights helpers -----------------------------------------------
  const addHighlight = () => {
    const h = highlightInput.trim();
    if (h && !formData.highlights.includes(h)) {
      setFormData(prev => ({ ...prev, highlights: [...prev.highlights, h] }));
      setHighlightInput('');
    }
  };

  const removeHighlight = (h) => setFormData(prev => ({
    ...prev, highlights: prev.highlights.filter(x => x !== h)
  }));

  const toggleIndustry = (id) => setFormData(prev => ({
    ...prev,
    industries: prev.industries.includes(id)
      ? prev.industries.filter(i => i !== id)
      : [...prev.industries, id],
  }));

  // ---- Industry group / select-all helpers ------------------------------
  const allIndustryIds = STANDARD_INDUSTRIES.map(i => i.id);
  const allIndustriesSelected =
    allIndustryIds.length > 0 && allIndustryIds.every(id => formData.industries.includes(id));

  const setIndustries = (ids) => setFormData(prev => ({ ...prev, industries: ids }));

  const categoryIndustryIds = (catId) =>
    STANDARD_INDUSTRIES.filter(i => i.category === catId).map(i => i.id);

  const isCategoryFullySelected = (catId) => {
    const ids = categoryIndustryIds(catId);
    return ids.length > 0 && ids.every(id => formData.industries.includes(id));
  };

  const toggleCategory = (catId) => {
    const ids = categoryIndustryIds(catId);
    setFormData(prev => {
      const fully = ids.every(id => prev.industries.includes(id));
      return {
        ...prev,
        industries: fully
          ? prev.industries.filter(id => !ids.includes(id))
          : Array.from(new Set([...prev.industries, ...ids])),
      };
    });
  };

  // ---- Submit -----------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error('Title is required'); return; }

    setLoading(true);
    try {
      const isInsight = formData.type === 'insight';
      const payload = {
        title: formData.title,
        type: formData.type,
        country: formData.country,
        industries: formData.industries,
        price: isInsight ? 0 : Number(formData.price) || 299,
        pages: Number(formData.pages) || 30,
        summary: formData.summary,
        highlights: formData.highlights,
        is_featured: formData.is_featured,
        pdf_url: formData.pdf_url || null,
        related_report_id: formData.related_report_id || null,
        temp_file_id: tempFileId || null,
      };

      const url = isEdit
        ? `${BACKEND_URL}/api/admin/reports/${report._id}`
        : `${BACKEND_URL}/api/admin/reports`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');

      toast.success(isEdit ? 'Report updated' : 'Report created');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = isEdit
    ? formData.title.trim().length > 0
    : aiState === 'done' || showAllFields;

  // ---- Render -----------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? (
              <><FileText className="w-5 h-5 text-primary" /> Edit Report</>
            ) : (
              <><Sparkles className="w-5 h-5 text-primary" /> Create Report</>
            )}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-1">

          {/* ===== CREATE MODE — AI Upload Zone ===== */}
          {!isEdit && aiState === 'idle' && !showAllFields && (
            <div className="space-y-4">
              {/* Drop zone */}
              <div
                className="border-2 border-dashed border-primary/30 rounded-xl p-8 text-center cursor-pointer hover:border-primary/60 hover:bg-primary/5 transition-all"
                onClick={() => analyzeInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleAnalyzePdf(f); }}
              >
                <Sparkles className="w-10 h-10 text-primary mx-auto mb-3 opacity-70" />
                <p className="font-semibold text-base mb-1">Upload PDF — AI will extract report info</p>
                <p className="text-sm text-muted-foreground">
                  Title, summary, highlights, page count and metadata auto-filled
                </p>
                <p className="text-xs text-muted-foreground mt-2">Click to browse or drag & drop</p>
                <input
                  ref={analyzeInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleAnalyzePdf(f); }}
                />
              </div>
            </div>
          )}

          {/* ===== UPLOADING SPINNER ===== */}
          {!isEdit && aiState === 'uploading' && (
            <div className="py-12 text-center space-y-4">
              <div className="relative w-16 h-16 mx-auto">
                <Loader2 className="w-16 h-16 text-primary animate-spin" />
                <Sparkles className="w-6 h-6 text-primary absolute inset-0 m-auto" />
              </div>
              <p className="font-semibold">Analysing <span className="text-primary">{pdfFilename}</span>…</p>
              <p className="text-sm text-muted-foreground">
                Extracting text and generating report metadata with AI
              </p>
            </div>
          )}

          {/* ===== ERROR STATE ===== */}
          {!isEdit && aiState === 'error' && (
            <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5 flex gap-3">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm">AI extraction failed</p>
                <p className="text-xs text-muted-foreground mb-3">Please retry with the same or another PDF.</p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => { setAiState('idle'); setPdfFilename(null); }}>
                    Try another PDF
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ===== MAIN FORM (after extraction or manual / edit mode) ===== */}
          {(isEdit || aiState === 'done' || showAllFields) && (
            <>
              {/* AI Extracted banner */}
              {!isEdit && aiState === 'done' && (
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/25 rounded-lg text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                    AI extracted from <span className="font-semibold">{pdfFilename}</span> — review and save
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-6 text-xs"
                    onClick={() => { setAiState('idle'); setTempFileId(null); setPdfFilename(null); setFormData({ ...EMPTY_FORM }); setShowAllFields(false); }}
                  >
                    Start over
                  </Button>
                </div>
              )}

              {/* ── REQUIRED: Price + Industries ── */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price" className="font-semibold">
                    Price (USD) <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.price}
                      onChange={e => setFormData(p => ({ ...p, price: e.target.value }))}
                      className="pl-7"
                      data-testid="report-price-input"
                    />
                  </div>
                  {formData.type === 'insight' && (
                    <p className="text-xs text-muted-foreground">Insight reports are always free</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">
                    Industries <span className="text-destructive">*</span>
                  </Label>
                  <Popover open={showIndustryPicker} onOpenChange={setShowIndustryPicker}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full min-h-10 h-auto justify-between flex-wrap gap-1 py-2 px-3"
                        data-testid="report-industry-trigger"
                      >
                        <div className="flex flex-wrap gap-1 flex-1 text-left">
                          {formData.industries.length > 0 ? (
                            formData.industries.map(id => (
                              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                                {getIndustryLabel(id, 'en')}
                                <X className="w-3 h-3 cursor-pointer hover:text-destructive"
                                  onClick={e => { e.stopPropagation(); toggleIndustry(id); }} />
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">Select industries…</span>
                          )}
                        </div>
                        <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="start">
                      <div className="flex items-center justify-between px-3 py-2 border-b">
                        <span className="text-xs font-semibold text-muted-foreground" data-testid="report-industry-count">
                          {formData.industries.length} selected
                        </span>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs px-2"
                            onClick={() => setIndustries(allIndustriesSelected ? [] : [...allIndustryIds])}
                            data-testid="report-industry-select-all"
                          >
                            {allIndustriesSelected ? 'Clear all' : 'Select all'}
                          </Button>
                        </div>
                      </div>
                      <ScrollArea className="h-[300px]">
                        <div className="p-2">
                          {INDUSTRY_CATEGORIES.map(cat => {
                            const catInds = STANDARD_INDUSTRIES.filter(i => i.category === cat.id);
                            if (!catInds.length) return null;
                            return (
                              <div key={cat.id} className="mb-3">
                                <div
                                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground px-2 py-1.5 bg-muted/50 rounded cursor-pointer hover:bg-muted"
                                  onClick={() => toggleCategory(cat.id)}
                                  data-testid={`report-industry-category-${cat.id}`}
                                >
                                  <Checkbox checked={isCategoryFullySelected(cat.id)} readOnly />
                                  <span>{cat.en}</span>
                                </div>
                                <div className="mt-1 space-y-1">
                                  {catInds.map(ind => (
                                    <div
                                      key={ind.id}
                                      className="flex items-center space-x-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer"
                                      onClick={() => toggleIndustry(ind.id)}
                                      data-testid={`report-industry-${ind.id}`}
                                    >
                                      <Checkbox checked={formData.industries.includes(ind.id)} readOnly />
                                      <span className="text-sm">{ind.en}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* ── AI-extracted fields (collapsible in AI mode) ── */}
              {!isEdit && aiState === 'done' && (
                <div>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full mb-3"
                    onClick={() => setShowAllFields(v => !v)}
                  >
                    {showAllFields ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {showAllFields ? 'Hide' : 'Review & edit'} AI-extracted fields
                  </button>
                </div>
              )}

              {(isEdit || showAllFields || aiState !== 'done') && (
                <div className="space-y-4 border-t pt-4">

                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title">Title <span className="text-destructive">*</span></Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                      placeholder="Report title"
                      data-testid="report-title-input"
                      required
                    />
                  </div>

                  {/* Type + Country */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={formData.type} onValueChange={v => setFormData(p => ({ ...p, type: v }))}>
                        <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {REPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.en}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Country / Region</Label>
                      <Select value={formData.country} onValueChange={v => setFormData(p => ({ ...p, country: v }))}>
                        <SelectTrigger data-testid="report-country-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.en}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Pages */}
                  <div className="space-y-2">
                    <Label htmlFor="pages">Pages</Label>
                    <Input
                      id="pages"
                      type="number"
                      min="1"
                      value={formData.pages}
                      onChange={e => setFormData(p => ({ ...p, pages: e.target.value }))}
                      data-testid="report-pages-input"
                    />
                  </div>

                  {/* Summary */}
                  <div className="space-y-2">
                    <Label htmlFor="summary">Summary</Label>
                    <Textarea
                      id="summary"
                      value={formData.summary}
                      onChange={e => setFormData(p => ({ ...p, summary: e.target.value }))}
                      rows={3}
                      placeholder="Brief description of the report…"
                      data-testid="report-summary-input"
                    />
                  </div>

                  {/* Highlights */}
                  <div className="space-y-2">
                    <Label>Key Highlights</Label>
                    <div className="space-y-2">
                      {formData.highlights.map((h, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 bg-muted/50 rounded text-sm">
                          <span className="flex-1">{h}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 hover:text-destructive"
                            onClick={() => removeHighlight(h)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input
                          value={highlightInput}
                          onChange={e => setHighlightInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHighlight(); } }}
                          placeholder="Add a key highlight…"
                          data-testid="report-highlight-input"
                        />
                        <Button type="button" variant="outline" onClick={addHighlight} data-testid="add-highlight-btn">Add</Button>
                      </div>
                    </div>
                  </div>

                  {/* Featured */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="featured"
                      checked={formData.is_featured}
                      onCheckedChange={v => setFormData(p => ({ ...p, is_featured: v }))}
                      data-testid="report-featured-checkbox"
                    />
                    <Label htmlFor="featured">Featured report</Label>
                  </div>

                  {/* PDF management (edit mode only) */}
                  {isEdit && (
                    <div className="space-y-2">
                      <Label>PDF File</Label>
                      {pdfInfo.hasFile ? (
                        <div className="flex items-center gap-3 p-3 border rounded-lg">
                          <FileText className="w-5 h-5 text-primary shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{pdfInfo.filename}</p>
                            {pdfInfo.size && (
                              <p className="text-xs text-muted-foreground">{(pdfInfo.size / 1024).toFixed(1)} KB</p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={handlePdfDelete}
                            disabled={pdfUploading}
                          >
                            {pdfUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                          onClick={() => pdfInputRef.current?.click()}
                        >
                          <Upload className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Click to upload PDF</p>
                          <input
                            ref={pdfInputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handlePdfUpload(f); }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* WO-63 — white-label Word file (edit mode, paid reports only) */}
                  {isEdit && formData.type !== 'insight' && (
                    <div className="space-y-2" data-testid="whitelabel-docx-section">
                      <Label>White-label Word file (Enterprise)</Label>
                      <p className="text-xs text-muted-foreground">
                        An editable .docx with no Mid2Bid or Pastoria name or logo. The app checks the text,
                        headers, footers, charts and links and removes the hidden author details; it cannot read pictures.
                      </p>
                      {docxInfo.hasFile ? (
                        <div className="flex items-center gap-3 p-3 border rounded-lg">
                          <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{docxInfo.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {docxInfo.size ? `${(docxInfo.size / 1024).toFixed(1)} KB` : ''}
                              {docxInfo.uploadedAt ? ` · uploaded ${new Date(docxInfo.uploadedAt).toLocaleDateString()}` : ''}
                            </p>
                            {docxInfo.pictures > 0 && (
                              <p className="text-xs text-amber-600">
                                {docxInfo.pictures} picture{docxInfo.pictures === 1 ? '' : 's'} in the file — make sure none is the Mid2Bid logo.
                              </p>
                            )}
                          </div>
                          <Button type="button" variant="outline" size="sm" disabled={docxUploading}
                            onClick={() => docxInputRef.current?.click()}>
                            Replace
                          </Button>
                          <Button type="button" variant="ghost" size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={handleDocxDelete} disabled={docxUploading}>
                            {docxUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                          onClick={() => docxInputRef.current?.click()}
                        >
                          {docxUploading
                            ? <Loader2 className="w-6 h-6 mx-auto mb-1 animate-spin text-muted-foreground" />
                            : <Upload className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />}
                          <p className="text-sm text-muted-foreground">Click to upload the white-label Word file (.docx)</p>
                        </div>
                      )}
                      <input
                        ref={docxInputRef}
                        type="file"
                        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleDocxUpload(f); }}
                      />
                      {docxProblems && (
                        <div className="p-3 border border-destructive/50 bg-destructive/5 rounded-lg text-sm space-y-1" data-testid="whitelabel-docx-problems">
                          <p className="font-medium text-destructive flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />{docxProblems.message}
                          </p>
                          {docxProblems.problems.map((p, i) => (
                            <p key={i} className="text-xs text-muted-foreground whitespace-pre-wrap break-words">{p}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Footer */}
          {(isEdit || aiState === 'done' || showAllFields) && (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !canSubmit}
                data-testid="save-report-btn"
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  : isEdit ? 'Update Report' : 'Create Report'}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminReportDialog;
